import { dashboardT } from '../../../../../../lib/messages';
import { getFormTabErrorCounts } from '../../../../../../lib/formTabErrors';
import { useEffect, useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BadgeDollarSign,
  Database,
  Gauge,
  Gavel,
  ListFilter,
  RefreshCcw,
  Settings,
  WalletCards,
} from 'lucide-react';
import { useForm, useWatch, type FieldErrors } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormTab,
  DashboardPageFormTabList,
  DashboardPageFormTabPanel,
} from '@shigo-ui/dashboard-ui';
import { Button, Form } from '@shigo-ui/ui';

import { EditorSectionTabs } from '../../../../../../components/forms/EditorSectionTabs';
import { useEditorSectionControls } from '../../../../../../components/forms/useEditorSection';
import {
  applyDemandPartnerBidderConfig,
  fallbackDemandPartnerBidderConfig,
  resolveDemandPartnerBidderConfig,
} from '../../../bidderConfig';
import { demandPartnerFormSchema, type DemandPartnerFormValue } from '../../../formSchema';
import { DemandPartnerDynamicField, DemandPartnerEditorTab } from '../../../model';
import { useDemandPartnerBidderConfigQuery } from '../../../hooks';
import { DemandPartnerGeneralFields } from '../GeneralFields';
import { DemandPartnerBiddingFields, DemandPartnerPricingFields } from '../PricingBiddingFields';
import {
  DemandPartnerDeliveryCapFields,
  DemandPartnerUserSyncFields,
} from '../DeliveryUserSyncFields';
import { DemandPartnerCacheFields, DemandPartnerQpsFields } from '../QpsCacheFields';
import { DemandPartnerBundleDomainLists } from '../DemandPartnerBundleDomainLists';

const tabs = [
  { value: DemandPartnerEditorTab.General, label: 'General', icon: Settings },
  { value: DemandPartnerEditorTab.Pricing, label: 'Pricing', icon: BadgeDollarSign },
  { value: DemandPartnerEditorTab.Bidding, label: 'Bidding', icon: Gavel },
  { value: DemandPartnerEditorTab.DeliveryCap, label: 'Delivery cap', icon: WalletCards },
  { value: DemandPartnerEditorTab.UserSync, label: 'User sync', icon: RefreshCcw },
  { value: DemandPartnerEditorTab.Qps, label: 'QPS', icon: Gauge },
  {
    value: DemandPartnerEditorTab.BundleDomainLists,
    label: 'Bundle and Domain lists',
    icon: ListFilter,
  },
] as const;

const demandPartnerDynamicFields: ReadonlySet<string> = new Set(
  Object.values(DemandPartnerDynamicField),
);

export function DemandPartnerEditorForm(props: {
  initialValue: DemandPartnerFormValue;
  bundleListCount: number;
  domainListCount: number;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (value: DemandPartnerFormValue) => Promise<void>;
}) {
  'use no memo';
  const form = useForm<DemandPartnerFormValue>({
    defaultValues: props.initialValue,
    resolver: zodResolver(demandPartnerFormSchema),
  });
  const bidderCode = useWatch({ control: form.control, name: 'bidderCode' });
  const configQuery = useDemandPartnerBidderConfigQuery();
  const config = configQuery.data ?? fallbackDemandPartnerBidderConfig;
  const resolved = useMemo(
    () => resolveDemandPartnerBidderConfig(bidderCode, config),
    [bidderCode, config],
  );
  // `useWatch` starts from the form value, so this is the bidder the form opened with.
  const previousBidder = useRef(bidderCode);
  const showCache = resolved.visible.includes(DemandPartnerDynamicField.CacheParams);
  const [editorSection, setActiveTab] = useEditorSectionControls(
    DemandPartnerEditorTab.General,
    Object.values(DemandPartnerEditorTab).filter(
      (tab) => showCache || tab !== DemandPartnerEditorTab.Cache,
    ),
  );

  useEffect(() => {
    const bidderChanged = previousBidder.current !== bidderCode;
    previousBidder.current = bidderCode;
    if (bidderChanged) {
      const configured = applyDemandPartnerBidderConfig(form.getValues(), config);
      form.setValue('additionalCost', configured.additionalCost, { shouldDirty: true });
      form.setValue('cacheParamsEnabled', configured.cacheParamsEnabled, { shouldDirty: true });
      form.setValue('cacheUa', configured.cacheUa, { shouldDirty: true });
      form.setValue('cacheIp', configured.cacheIp, { shouldDirty: true });
      form.setValue('cacheBundle', configured.cacheBundle, { shouldDirty: true });
      form.setValue('cacheCountry', configured.cacheCountry, { shouldDirty: true });
      form.setValue('cacheCity', configured.cacheCity, { shouldDirty: true });
    }
    form.setValue(
      'requiredDynamicFields',
      resolved.required.filter((field): field is DemandPartnerDynamicField =>
        demandPartnerDynamicFields.has(field),
      ),
    );
    form.setValue('cacheParamsVisible', showCache);
  }, [bidderCode, config, form, resolved.required, showCache]);

  function invalidSubmit(errors: FieldErrors<DemandPartnerFormValue>) {
    if (
      errors.pricingType ||
      errors.revShareValue ||
      errors.ioCpmCostValue ||
      errors.additionalCost ||
      errors.revShareGrossNet
    )
      setActiveTab(DemandPartnerEditorTab.Pricing);
    else if (errors.biddingGrossNet || errors.biddingTmax || errors.biddingBidTTL)
      setActiveTab(DemandPartnerEditorTab.Bidding);
    else if (errors.revenueCap) setActiveTab(DemandPartnerEditorTab.DeliveryCap);
    else if (errors.userSyncRedirect || errors.userSyncIframe)
      setActiveTab(DemandPartnerEditorTab.UserSync);
    else if (errors.qps) setActiveTab(DemandPartnerEditorTab.Qps);
    else setActiveTab(DemandPartnerEditorTab.General);
  }

  const visibleTabs = showCache
    ? [...tabs, { value: DemandPartnerEditorTab.Cache, label: 'Cache', icon: Database }]
    : tabs;
  const tabErrors = getFormTabErrorCounts(
    form.formState.errors,
    {
      [DemandPartnerEditorTab.Pricing]: [
        'pricingType',
        'revShareValue',
        'ioCpmCostValue',
        'additionalCost',
        'revShareGrossNet',
        'useStickyPricing',
      ],
      [DemandPartnerEditorTab.Bidding]: ['biddingGrossNet', 'biddingTmax', 'biddingBidTTL'],
      [DemandPartnerEditorTab.DeliveryCap]: ['revenueCap', 'revenueCapEnabled'],
      [DemandPartnerEditorTab.UserSync]: [
        'userSyncRedirect',
        'userSyncIframe',
        'userSyncRedirectEnabled',
        'userSyncIframeEnabled',
        'includeUserIdInCacheKey',
      ],
      [DemandPartnerEditorTab.Qps]: ['qps'],
      [DemandPartnerEditorTab.Cache]: [
        'cacheParamsEnabled',
        'cacheUa',
        'cacheIp',
        'cacheBundle',
        'cacheCountry',
        'cacheCity',
      ],
    },
    DemandPartnerEditorTab.General,
  );

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={(event) => void form.handleSubmit(props.onSubmit, invalidSubmit)(event)}
      >
        <EditorSectionTabs controls={editorSection}>
          <DashboardPageFormTabList>
            {visibleTabs.map(({ value, label, icon: Icon }) => (
              <DashboardPageFormTab
                key={value}
                value={value}
                errorCount={tabErrors[value]}
                errorLabel={(count) => dashboardT('common.validationErrors', { count })}
                icon={<Icon className="h-4 w-4" aria-hidden="true" />}
              >
                {label}
              </DashboardPageFormTab>
            ))}
          </DashboardPageFormTabList>
          {visibleTabs.map(({ value }) => (
            <DashboardPageFormTabPanel key={value} value={value}>
              <DemandPartnerEditorFields
                tab={value}
                partnerId={props.initialValue.id}
                bundleListCount={props.bundleListCount}
                domainListCount={props.domainListCount}
              />
            </DashboardPageFormTabPanel>
          ))}
        </EditorSectionTabs>
        <DashboardPageFormActions>
          <Button type="button" variant="outline" disabled={props.saving} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={props.saving}>
            {props.saving ? 'Saving…' : 'Save'}
          </Button>
        </DashboardPageFormActions>
      </form>
    </Form>
  );
}

function DemandPartnerEditorFields(props: {
  tab: DemandPartnerEditorTab;
  partnerId: string;
  bundleListCount: number;
  domainListCount: number;
}) {
  const { tab } = props;
  if (tab === DemandPartnerEditorTab.Pricing) return <DemandPartnerPricingFields />;
  if (tab === DemandPartnerEditorTab.Bidding) return <DemandPartnerBiddingFields />;
  if (tab === DemandPartnerEditorTab.DeliveryCap) return <DemandPartnerDeliveryCapFields />;
  if (tab === DemandPartnerEditorTab.UserSync) return <DemandPartnerUserSyncFields />;
  if (tab === DemandPartnerEditorTab.Qps) return <DemandPartnerQpsFields />;
  if (tab === DemandPartnerEditorTab.Cache) return <DemandPartnerCacheFields />;
  if (tab === DemandPartnerEditorTab.BundleDomainLists)
    return (
      <DemandPartnerBundleDomainLists
        partnerId={props.partnerId}
        bundleListCount={props.bundleListCount}
        domainListCount={props.domainListCount}
      />
    );
  return <DemandPartnerGeneralFields />;
}
